import { Component, OnDestroy, OnInit, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, takeUntil, tap } from 'rxjs/operators';
import { CustomerApiService, CustomerDto } from '../../../services/customer-api.service';

export interface CaseCreateDialogData {
  customerId?: number;
  customerName?: string;
}

@Component({
  selector: 'app-case-create-dialog',
  templateUrl: './case-create-dialog.component.html',
  styleUrls: ['./case-create-dialog.component.css'],
})
export class CaseCreateDialogComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  saving = false;
  users: { id: number; name: string }[] = [];
  hasPrefilledCustomer = false;

  // Customer picker (autocomplete) — user selects a customer by name, not by ID.
  customerCtrl = new FormControl<string | CustomerDto | null>('');
  customerOptions: CustomerDto[] = [];
  displayCustomer = (c: CustomerDto | string | null): string =>
    c && typeof c !== 'string' ? c.displayName : (c ?? '');
  private destroy$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<CaseCreateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CaseCreateDialogData,
    private snackBar: MatSnackBar,
    private customerApi: CustomerApiService,
  ) {}

  ngOnInit(): void {
    this.hasPrefilledCustomer = !!this.data.customerId;
    this.form = this.fb.group({
      title:            ['', [Validators.required, Validators.maxLength(255)]],
      description:      ['', Validators.maxLength(2000)],
      relatedRef:       ['', Validators.maxLength(128)],
      assignedToUserId: [null],
      customerId:       [
        this.data.customerId ?? null,
        this.hasPrefilledCustomer ? [] : [Validators.required]
      ],
      followUpOn:       [null],
      followUpTime:     [''],
    });
    this.loadUsers();
    if (!this.hasPrefilledCustomer) {
      this.customerCtrl.valueChanges.pipe(
        tap(value => {
          if (typeof value === 'string') this.form.patchValue({ customerId: null });
        }),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(value => {
          if (typeof value !== 'string') return of([]);
          if (value.trim().length < 2) return of([]);
          return this.customerApi.searchCustomers(value.trim()).pipe(catchError(() => of([])));
        }),
        takeUntil(this.destroy$),
      ).subscribe(options => { this.customerOptions = options; });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadUsers(): void {
    // /api/users returns a bare array of users; usernames are email-style, so show
    // the local part as the name.
    this.http.get<{ id: number; userName: string | null }[]>('/api/users').subscribe({
      next: list => {
        this.users = (list ?? []).map(u => {
          const n = u.userName ?? `User ${u.id}`;
          return { id: u.id, name: n.includes('@') ? n.split('@')[0] : n };
        });
      },
      error: () => {}
    });
  }

  onCustomerSelected(c: CustomerDto): void {
    this.form.patchValue({ customerId: c.id });
  }

  submit(): void {
    if (this.form.invalid) return;
    this.saving = true;
    const v = this.form.value;
    const payload = {
      ...v,
      followUpOn: v.followUpOn || null,
      // A time only means something with a date; send 'HH:mm:ss' for TimeOnly.
      followUpTime: v.followUpOn && v.followUpTime ? v.followUpTime + ':00' : null,
    };
    this.http.post('/api/cases', payload).subscribe({
      next: () => {
        this.saving = false;
        this.snackBar.open('Case created.', undefined, { duration: 2500 });
        this.dialogRef.close(true);
      },
      error: (e: any) => {
        this.saving = false;
        this.snackBar.open(e?.error?.message ?? 'Failed to create case.', 'OK', { duration: 5000 });
      }
    });
  }

  cancel(): void {
    this.dialogRef.close(false);
  }
}
